import { MutationCache, QueryClient } from "@tanstack/react-query";

import { onSessionCleared } from "./client";
import { handleGlobalMutationError } from "@/lib/globalMutationError";

/**
 * THE app's QueryClient, in a module of its own.
 *
 * It used to be a module-level const inside App.tsx and was NOT exported, so the
 * only way to reach it was the useQueryClient() hook -- which is React-only. That
 * is why signing out could not clear the cache: clearTokens() lives in
 * api/client.ts, a plain module with no hooks and no handle on the client.
 *
 * Moved here rather than exported from App.tsx so api/client.ts can import it
 * without importing the whole application tree. There is no cycle: this module
 * imports only @tanstack/react-query and lib/globalMutationError, and that
 * imports only the toast component.
 */
export const queryClient = new QueryClient({
  // Safety net for the whole app: any mutation that fails WITHOUT its own
  // onError (and without opting out via meta.silent) used to fail completely
  // silently — the user clicked, nothing happened, no feedback. This surfaces a
  // toast for those cases only, so mutations that already handle their error
  // (inline UI or their own onError) never double-report.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      handleGlobalMutationError(error, mutation),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Signing out must drop every cached response, not just the tokens (row
 * 1f6beae0, founder answer 581386ab "YES - fix it").
 *
 * Registered HERE rather than called from the sign-out buttons because there are
 * FOUR paths that sign a user out -- Topbar, ActivityRibbon, FelixChat and
 * UserProfile -- and the row named only three; the fourth was found while
 * re-verifying it. All four call clearTokens(), so subscribing to that is the one
 * place a future fifth path cannot miss.
 *
 * clear() rather than removeQueries/resetQueries: those leave observers attached
 * and can refetch the same keys straight back under the new session.
 */
onSessionCleared(() => queryClient.clear());
